import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";

/**
 * Marks a single Work Order as viewed, clearing the caller's unread item. Fires
 * when the user opens that specific record (list pane or full page), not when
 * the detail data loads: the header reads the same detail query, so clearing
 * on fetch would wipe items on every refetch. It intentionally skips the
 * success/error toast other Work Order mutations show: a toast on every open
 * would be noise, not feedback.
 */
export function useMarkWorkOrderViewedMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "work-orders", "mark-viewed"],
    mutationFn: async (workOrderId: WorkOrderId) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }

      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"]["mark-viewed"].$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId: String(workOrderId),
        },
      });
      if (!response.ok) {
        throw new Error(
          `Failed to mark the Work Order as viewed: ${response.status}`,
        );
      }
    },
    onSuccess: () => {
      // One prefix invalidation refreshes the unread count and clears the stale
      // `isUnread` flag cached on the list rows; the count key sits under it.
      void queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
}
